import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
  Avatar,
  Grid,
  IconButton,
  TableFooter,
  Box,
  Tooltip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import MainLoader from "../../../components/loading/MainLoader";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import Title from "../../../components/title/Title";
import { useNavigate, useParams } from "react-router-dom";
import CloudSyncTwoToneIcon from "@mui/icons-material/CloudSyncTwoTone";
import ShareLocationIcon from "@mui/icons-material/ShareLocation";
import { getCustomerReferenceNumber } from "../../../services/localStorage";
import { BASE_URL } from "../../../const/URL";
import { useLocation } from "react-router-dom";

interface Service {
  accountSegment: string;
  billingAccountNumber: string;
  bandwidth: string;
  customerAccountNumber: string;
  productName: string;
  sltActualDSPDate: string;
  serviceAddrCity: string;
  serviceID: string;
  status: string;
  relocation: any;
  usageReports: any;
}

const ServicesTable = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { productName } = useParams<{ productName: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [services, setServices] = useState<Service[]>([]);
  const [filteredServices, setFilteredServices] = useState<Service[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>("all");

  const relocationStatus = (state as any)?.relocation;
  const modificationStatus = (state as any)?.modification;
  const displayProductName = (state as any)?.displayName;
  const usageReports = (state as any)?.usageReports;

  const fetchServices = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("enterprise_portal_access_Token");
      const response = await axios.post(
        `${BASE_URL}/api/v1/ENTCustomCRMAPI/api/records-by-product/${productName}`,
        {
          CustomerAccountNumber: getCustomerReferenceNumber(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setServices(response.data);
      setFilteredServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  // Apply filters whenever search term, status, or date filters change
  useEffect(() => {
    let result = services;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (service) =>
          service.productName.toLowerCase().includes(term) ||
          service.serviceID.toLowerCase().includes(term) ||
          service.customerAccountNumber.toLowerCase().includes(term) ||
          service.serviceAddrCity.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      result = result.filter((service) => service.status === statusFilter);
    }

    // Apply date filter
    if (dateFilter !== "all") {
      const today = new Date();
      result = result.filter((service) => {
        const serviceDate = new Date(service.sltActualDSPDate);
        switch (dateFilter) {
          case "today":
            return serviceDate.toDateString() === today.toDateString();
          case "thisWeek":
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() - today.getDay());
            return serviceDate >= weekStart;
          case "thisMonth":
            return (
              serviceDate.getMonth() === today.getMonth() &&
              serviceDate.getFullYear() === today.getFullYear()
            );
          case "thisYear":
            return serviceDate.getFullYear() === today.getFullYear();
          case "older":
            return serviceDate < today;
          default:
            return true;
        }
      });
    }

    setFilteredServices(result);
    setPage(0); // Reset to first page when filters change
  }, [searchTerm, statusFilter, dateFilter, services]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRelocation = (customerAccountNumber: string) => {
    navigate("/serviceRequest/serviceRelocation", {
      state: { customerAccountNumber },
    });
  };

  const handleModification = (customerAccountNumber: string) => {
    navigate("/serviceRequest/serviceModification", {
      state: { customerAccountNumber },
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Get unique statuses for filter dropdown
  const statusOptions = [
    "all",
    ...Array.from(new Set(services.map((s) => s.status))),
  ];
  const dateOptions = [
    { value: "all", label: "All Dates" },
    { value: "today", label: "Today" },
    { value: "thisWeek", label: "This Week" },
    { value: "thisMonth", label: "This Month" },
    { value: "thisYear", label: "This Year" },
    { value: "older", label: "Older" },
  ];

  return (
    <div className="page-container">
      <MainLoader show={loading} />

      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/serviceLineUp", title: "Service Line Up" },
          {
            path: `/serviceLineUp/${productName}`,
            title: `${displayProductName}`,
          },
        ]}
      />
      <Title>{displayProductName}</Title>

      {/* Filter Controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          padding: { xs: "10px 10px", sm: "10px 20px" },
          overflowX: "auto",
          gap: 2,
          mb: 1,
          flexWrap: { xs: "wrap", sm: "nowrap" },
          "& .MuiFormControl-root": {
            backgroundColor: "background.paper",
            borderRadius: 2,
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
            },
          },
          "& .MuiInputBase-root": {
            borderRadius: 2,
            "&:before, &:after": {
              borderBottom: "none !important",
            },
          },
          "& .MuiInputLabel-root": {
            color: "text.secondary",
            fontWeight: 500,
            transform: "translate(14px, 14px) scale(1)",
            "&.Mui-focused": {
              color: "primary.main",
              transform: "translate(14px, -9px) scale(0.75)",
            },
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "transparent",
            },
            "&:hover fieldset": {
              borderColor: "primary.light",
            },
            "&.Mui-focused fieldset": {
              borderColor: "primary.main",
              borderWidth: 1,
            },
          },
          "& .MuiSelect-select": {
            padding: "12px 32px 12px 14px",
          },
          "& .MuiMenuItem-root": {
            "&:hover": {
              backgroundColor: "primary.light",
              color: "primary.contrastText",
            },
          },
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Search services..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ color: "primary.main" }}>
                <SearchIcon />
              </InputAdornment>
            ),
            sx: {
              "& input": {
                padding: "12px 14px",
              },
            },
          }}
          sx={{
            minWidth: { xs: "100%", sm: 280 },
            "& .MuiInputBase-root": {
              backgroundColor: "background.paper",
            },
          }}
        />

        <FormControl sx={{ minWidth: { xs: "100%", sm: 180 } }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as string)}
            label="Status"
            startAdornment={
              <InputAdornment
                position="start"
                sx={{ ml: 1, color: "primary.main" }}
              >
                <FilterListIcon fontSize="small" />
              </InputAdornment>
            }
            sx={{
              "& .MuiSelect-icon": {
                color: "primary.main",
              },
            }}
          >
            {statusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {status !== "all" && (
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor:
                          status === "Active"
                            ? "success.main"
                            : status === "Pending"
                            ? "warning.main"
                            : "error.main",
                      }}
                    />
                  )}
                  {status === "all" ? "All Status" : status}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: { xs: "100%", sm: 180 } }}>
          <Select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as string)}
            label="Date"
            sx={{
              "& .MuiSelect-icon": {
                color: "primary.main",
              },
            }}
          >
            {dateOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor:
                        option.value === "today"
                          ? "success.main"
                          : option.value === "thisWeek"
                          ? "info.main"
                          : option.value === "thisMonth"
                          ? "warning.main"
                          : option.value === "thisYear"
                          ? "success.main"
                          : option.value === "older"
                          ? "error.main"
                          : "text.disabled",
                    }}
                  />
                  {option.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {usageReports && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "left",
            width: "100%",
            marginLeft: "30px",
            padding: { xs: "0 10px", sm: "0 20px" },
            overflowX: "auto",
          }}
        >
          {usageReports?.split(",").map((link: string, index: number) => (
            <a
              key={index}
              href={link.trim()}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "6px 12px",
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                textDecoration: "none",
                color: "#1a73e8",
                fontSize: "13px",
                fontWeight: "500",
                backgroundColor: "#f8f9fa",
                transition: "all 0.2s ease",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e8f0fe";
                e.currentTarget.style.borderColor = "#d2e3fc";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f8f9fa";
                e.currentTarget.style.borderColor = "#e0e0e0";
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ flexShrink: 0 }}
              >
                <path
                  d="M10 6H6C4.89543 6 4 6.89543 4 8V18C4 19.1046 4.89543 20 6 20H16C17.1046 20 18 19.1046 18 18V14M14 4H20M20 4V10M20 4L10 14"
                  stroke="#1a73e8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Usage Report Link {index + 1}
            </a>
          ))}
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          padding: { xs: "0 10px", sm: "0 20px" },
          overflowX: "auto",
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 5,
            margin: "10px 0",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            width: "100%",
            "& .MuiTable-root": {
              minWidth: 300,
            },
            "& .MuiTablePagination-root": {
              display: "flex",
              alignItems: "baseline",
              justifyContent: "flex-end",
              minHeight: "56px",
              width: "400px",
              gap: "8px",
              flexWrap: "wrap",
            },
            "& .MuiTablePagination-spacer": {
              order: 2,
            },
            "& .MuiTablePagination-selectLabel": {
              order: 0,
              display: "flex",
              alignItems: "baseline",
              padding: "15px",
              height: "36px",
            },
            "& .MuiTablePagination-select": {
              order: 1,
              display: "flex",
              alignItems: "center",
              height: "36px",
            },
            "& .MuiTablePagination-displayedRows": {
              order: 3,
              display: "flex",
              padding: "15px",
              alignItems: "baseline",
              height: "36px",
            },
            "& .MuiTablePagination-actions": {
              order: 4,
              display: "flex",
              alignItems: "center",
              marginLeft: "auto",
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    width: "10%",
                  }}
                >
                  Service ID
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    width: "16%",
                  }}
                >
                  Account Number
                </TableCell>
                {filteredServices[0]?.bandwidth !== "N/A" && (
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      width: "16%",
                    }}
                  >
                    Bandwidth
                  </TableCell>
                )}

                <TableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    width: "16%",
                  }}
                >
                  Service Address City
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    width: "16%",
                  }}
                >
                  Activation Date (MM/DD/YYYY)
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    width: "16%",
                  }}
                >
                  Status
                </TableCell>
                {((relocationStatus !== "" && relocationStatus === "Yes") ||
                  (modificationStatus !== "" &&
                    modificationStatus === "Yes")) && (
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      width: "16%",
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredServices
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((service) => (
                  <TableRow key={service.serviceID}>
                    <TableCell>
                      {/* <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                          <Avatar
                            sx={{
                              backgroundColor: "secondary.main",
                              color: "secondary.contrastText",
                            }}
                          >
                            {displayProductName.charAt(0)}
                          </Avatar>
                        </Grid>
                        <Grid item>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {displayProductName}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {service.serviceID}
                          </Typography>
                        </Grid>
                      </Grid> */}
                      <Typography variant="body1">
                        {service.serviceID}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1">
                        {service.billingAccountNumber}
                      </Typography>
                      {/* <Typography variant="body2" color="textSecondary">
                        {service.accountSegment}
                      </Typography> */}
                    </TableCell>
                    {service?.bandwidth !== "N/A" && (
                      <TableCell>
                        <Typography variant="body1">
                          {service?.bandwidth}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body1">
                        {service.serviceAddrCity}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {formatDate(service.sltActualDSPDate)}
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                          color: "white",
                          borderRadius: 8,
                          padding: "3px 10px",
                          display: "inline-block",
                          backgroundColor:
                            service.status === "Active"
                              ? "success.main"
                              : service.status === "Pending"
                              ? "warning.main"
                              : "error.main",
                        }}
                      >
                        {service.status}
                      </Typography>
                    </TableCell>
                    {((relocationStatus !== "" && relocationStatus === "Yes") ||
                      (modificationStatus !== "" &&
                        modificationStatus === "Yes")) && (
                      <TableCell>
                        {relocationStatus !== "" &&
                          relocationStatus === "Yes" && (
                            <Tooltip
                              title="Relocate Service"
                              arrow
                              placement="top"
                              enterDelay={500}
                              leaveDelay={200}
                            >
                              <span>
                                <IconButton
                                  aria-label="relocate service"
                                  color="primary"
                                  sx={{
                                    margin: 0.5,
                                    textTransform: "none",
                                    "&:hover": {
                                      backgroundColor: "primary.light",
                                    },
                                  }}
                                  onClick={() =>
                                    handleRelocation(service.serviceID)
                                  }
                                >
                                  <ShareLocationIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                        {modificationStatus !== "" &&
                          modificationStatus === "Yes" && (
                            <Tooltip
                              title="Service Modification"
                              arrow
                              placement="top"
                              enterDelay={500}
                              leaveDelay={200}
                            >
                              <span>
                                <IconButton
                                  aria-label="modify service"
                                  color="primary"
                                  sx={{
                                    margin: 0.5,
                                    textTransform: "none",
                                    "&:hover": {
                                      backgroundColor: "primary.light",
                                    },
                                  }}
                                  onClick={() =>
                                    handleModification(service.serviceID)
                                  }
                                >
                                  <CloudSyncTwoToneIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                        {/* <Tooltip
                          title="Modify Service"
                          arrow
                          placement="top"
                          enterDelay={500}
                          leaveDelay={200}
                        >
                          <span>
                            <IconButton
                              aria-label="modify service"
                              color="secondary"
                              sx={{
                                margin: 0.5,
                                textTransform: "none",
                                "&:hover": {
                                  backgroundColor: "secondary.light",
                                },
                              }}
                              onClick={() =>
                                handleModification(
                                  service.customerAccountNumber
                                )
                              }
                            >
                              <CloudSyncTwoToneIcon />
                            </IconButton>
                          </span>
                        </Tooltip> */}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 15]}
                  count={filteredServices.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>
    </div>
  );
};

export default ServicesTable;
